import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Select } from "@/components/ui/select";
import { ApiError, api } from "@/lib/api";
import { Link, Navigate, createFileRoute, useLoaderData } from "@tanstack/react-router";
import { useCallback, useEffect, useState } from "react";

/**
 * `/suggestions/learnings` — admin-only review queue for the learning loop.
 *
 * What Hubert's own mining job found in its approve/reject history (rejected
 * suggestions with a reason, approved suggestions the admin edited first) as
 * short corrective patterns. A pattern is `draft` (mined, inert) until a
 * human Activates it — only then does it reach Hubert's prompt on future
 * analyses. Dismiss and Activate both stay fully reversible, at any time:
 * flipping status is the whole effect, nothing else to undo.
 *
 * A reviewer can also escalate a pattern beyond a prompt note: "Send to
 * Metoděj" opens a work item asking the junior-dev agent to fix it in
 * Hub's own analyst code instead of (or alongside) activating it as a
 * note — for patterns a prompt tweak alone can't really fix. Independent
 * of Activate/Dismiss; a learning can be both active and converted.
 *
 * See `server/routes/analyst-learnings.ts` for the API and
 * `server/services/analyst-learning/mine.ts` for how a draft is produced.
 */

export const Route = createFileRoute("/_app/suggestions/learnings")({
  component: LearningsPage,
});

type Learning = {
  id: string;
  text: string;
  status: "draft" | "active" | "dismissed";
  evidenceCount: number;
  generatedAt: string;
  project: { id: string; name: string } | null;
  reviewedBy: { id: string; name: string | null } | null;
  reviewedAt: string | null;
  convertedWorkItemId: string | null;
};

const TABS = [
  { key: "draft", label: "Draft" },
  { key: "active", label: "Active" },
  { key: "dismissed", label: "Dismissed" },
  { key: "all", label: "All" },
] as const;
type Tab = (typeof TABS)[number]["key"];

function LearningsPage() {
  const { user } = useLoaderData({ from: "/_app" });
  const [tab, setTab] = useState<Tab>("draft");
  const [rows, setRows] = useState<Learning[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [mining, setMining] = useState(false);
  const [mineMessage, setMineMessage] = useState<string | null>(null);
  const [projects, setProjects] = useState<Array<{ id: string; name: string }>>([]);
  // Which project each card's "send to Metoděj" picker currently has
  // selected — per-row, since different learnings may target different
  // codebases (though in practice it's almost always Hub's own).
  const [convertProject, setConvertProject] = useState<Record<string, string>>({});

  const load = useCallback((t: Tab) => {
    setRows(null);
    return api
      .get<{ learnings: Learning[] }>(`/api/analyst-learnings${t === "all" ? "" : `?status=${t}`}`)
      .then((res) => setRows(res.learnings))
      .catch(() => setError("Couldn't load learnings."));
  }, []);

  useEffect(() => {
    if (user.globalRole !== "admin") return;
    void load(tab);
  }, [user.globalRole, tab, load]);

  useEffect(() => {
    if (user.globalRole !== "admin") return;
    api
      .get<{ projects: Array<{ id: string; name: string }> }>("/api/projects?pageSize=200")
      .then((res) => setProjects(res.projects.map((p) => ({ id: p.id, name: p.name }))))
      .catch(() => {
        /* the picker just stays empty — convert stays disabled */
      });
  }, [user.globalRole]);

  async function decide(id: string, status: "active" | "dismissed") {
    setBusyId(id);
    try {
      await api.patch(`/api/analyst-learnings/${id}`, { status });
      await load(tab);
    } catch {
      setError("Couldn't save that decision.");
    } finally {
      setBusyId(null);
    }
  }

  /** Escalate a mined pattern to a real code fix: opens a work item
   *  assigned to Metoděj in whichever project the reviewer picked
   *  (almost always Hub's own codebase — the fix is always in Hub's
   *  analyst code, never the customer project a scoped learning is about). */
  async function convertToTask(id: string) {
    const projectId = convertProject[id];
    if (!projectId) return;
    setBusyId(id);
    try {
      const res = await api.post<{ workItemId: string }>(
        `/api/analyst-learnings/${id}/convert-to-task`,
        { projectId },
      );
      setRows(
        (prev) =>
          prev?.map((r) => (r.id === id ? { ...r, convertedWorkItemId: res.workItemId } : r)) ??
          prev,
      );
    } catch {
      setError("Couldn't send that to Metoděj.");
    } finally {
      setBusyId(null);
    }
  }

  async function mineNow() {
    setMining(true);
    setMineMessage(null);
    try {
      await api.post("/api/analyst-learnings/mine", {});
      setMineMessage("Mining started — check back shortly, or switch tabs to refresh.");
    } catch (err) {
      setMineMessage(
        err instanceof ApiError && err.status === 503
          ? "AI isn't configured."
          : "Couldn't start mining.",
      );
    } finally {
      setMining(false);
    }
  }

  if (user.globalRole !== "admin") {
    return <Navigate to="/" />;
  }

  return (
    <div className="space-y-4 p-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold">Hubert's Learnings</h1>
          <p className="text-sm text-muted-foreground">
            Patterns mined from confirmed corrections to Hubert's own past proposals — rejections
            with a reason, and approvals edited before applying. A pattern only reaches Hubert's
            prompt once you Activate it, and Deactivating is an immediate, total revert.
          </p>
        </div>
        <div className="flex flex-col items-end gap-1">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => void mineNow()}
            disabled={mining}
          >
            {mining ? "Starting…" : "Mine now"}
          </Button>
          {mineMessage && <p className="text-xs text-muted-foreground">{mineMessage}</p>}
        </div>
      </div>

      <div className="flex gap-1 border-b">
        {TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            onClick={() => setTab(t.key)}
            data-testid={`learnings-tab-${t.key}`}
            className={`-mb-px border-b-2 px-3 py-2 text-sm font-medium ${
              tab === t.key
                ? "border-primary text-foreground"
                : "border-transparent text-muted-foreground hover:text-foreground"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {error && <p className="text-sm text-destructive">{error}</p>}

      {rows && rows.length === 0 && (
        <p className="text-sm text-muted-foreground" data-testid="learnings-empty">
          {tab === "draft"
            ? 'Nothing waiting — run "Mine now" or wait for the weekly pass.'
            : "Nothing here."}
        </p>
      )}

      {rows?.map((r) => (
        <Card key={r.id} data-testid="learning-card">
          <CardContent className="space-y-2 pt-4">
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <Badge variant="secondary">{r.project ? r.project.name : "Global"}</Badge>
              <span>
                {r.evidenceCount} evidence item{r.evidenceCount === 1 ? "" : "s"}
              </span>
              <span>· mined {new Date(r.generatedAt).toLocaleDateString()}</span>
              {r.reviewedBy && (
                <span>
                  · {r.status === "active" ? "activated" : "dismissed"} by {r.reviewedBy.name}
                </span>
              )}
            </div>
            <p className="text-sm">{r.text}</p>
            <div className="flex gap-2">
              {r.status !== "active" && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => void decide(r.id, "active")}
                  disabled={busyId === r.id}
                  data-testid="activate-learning"
                >
                  Activate
                </Button>
              )}
              {r.status !== "dismissed" && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => void decide(r.id, "dismissed")}
                  disabled={busyId === r.id}
                  data-testid="dismiss-learning"
                >
                  Dismiss
                </Button>
              )}
            </div>
            <div className="flex items-center gap-2 border-t pt-2">
              {r.convertedWorkItemId ? (
                <span className="text-xs text-muted-foreground">
                  Sent to Metoděj —{" "}
                  <Link
                    to="/work-items/$id"
                    params={{ id: r.convertedWorkItemId }}
                    className="underline"
                  >
                    open ticket
                  </Link>
                </span>
              ) : (
                <>
                  <Select
                    size="sm"
                    aria-label="Target project for the code fix"
                    value={convertProject[r.id] ?? ""}
                    onChange={(v) => setConvertProject((prev) => ({ ...prev, [r.id]: v }))}
                    options={[
                      { value: "", label: "— pick Hub's own project —" },
                      ...projects.map((p) => ({ value: p.id, label: p.name })),
                    ]}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => void convertToTask(r.id)}
                    disabled={busyId === r.id || !convertProject[r.id]}
                    data-testid="convert-to-task"
                  >
                    Send to Metoděj
                  </Button>
                </>
              )}
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
